import { useCallback, useEffect, useState } from 'react'
import * as API from '../../utility/api'
import moment from 'moment'
import { isHandledOverpaid, isUnhandledOverpaid } from './overpaid'

const ACTIONS = {
  TICKETS_OVERPAID: 'tickets_overpaid'
}

const Tools = ({ onOverpaidChange }) => {
  const [action, setAction] = useState(ACTIONS.TICKETS_OVERPAID)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [loadError, setLoadError] = useState(null)

  const updateData = useCallback(nextData => {
    setData(previous =>
      typeof nextData === 'function' ? nextData(previous) : nextData
    )
  }, [])

  useEffect(() => {
    if (!data || !onOverpaidChange) {
      return
    }
    onOverpaidChange(data.tickets)
  }, [data, onOverpaidChange])

  const load = useCallback(
    requestedAction => {
      setLoadError(null)
      setLoading(true)

      API.GET(`admin/tools/${requestedAction}`, {})
        .then(({ data: result }) => {
          updateData(result)
          setLoadError(null)
          setLoading(false)
        })
        .catch(error => {
          console.error(error)
          setLoadError('Tietojen lataus epäonnistui.')
          setLoading(false)
        })
    },
    [updateData]
  )

  useEffect(() => {
    load(ACTIONS.TICKETS_OVERPAID)
  }, [load])

  const fetchData = e => {
    e.preventDefault()
    load(action)
  }

  return (
    <div>
      <form onSubmit={fetchData}>
        <fieldset>
          <legend>Toiminto</legend>
          <div className="field field--inline">
            <input
              id="tickets-overpaid"
              type="radio"
              checked={action === ACTIONS.TICKETS_OVERPAID}
              onChange={() => setAction(ACTIONS.TICKETS_OVERPAID)}
            />
            <label
              htmlFor="tickets-overpaid"
              style={{
                textTransform: 'none',
                letterSpacing: 0,
                fontSize: 14,
                fontWeight: 400,
                color: 'var(--lp-text)',
                margin: 0
              }}
            >
              Liput, joissa on liikaa maksuja
            </label>
          </div>
          <div style={{ marginTop: 16 }}>
            <button className="btn btn--primary" disabled={loading} type="submit">
              {loading ? 'Ladataan…' : 'Päivitä'}
            </button>
          </div>
        </fieldset>
      </form>
      {loadError && (
        <p className="field__error" role="alert">
          {loadError}
        </p>
      )}
      <ToolsResult data={data} setData={updateData} />
    </div>
  )
}

const ToolsResult = ({ data, setData }) => {
  return toolsResultOverpaidTickets(data, setData)
}

const formatTimestamp = value => {
  if (!value) {
    return ''
  }

  const parsed = moment(new Date(value))
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : ''
}

const toolsResultOverpaidTickets = (data, setData) => {
  if (!data) {
    return null
  }

  const toggleHandled = ticket => {
    const nextHandled = isUnhandledOverpaid(ticket)

    API.PUT('admin/tools/tickets_overpaid/handled', {
      order_product_id: ticket.order_product_id,
      handled: nextHandled
    })
      .then(({ data: result }) => {
        setData(previous => ({
          ...previous,
          tickets: previous.tickets.map(row =>
            row.order_product_id === ticket.order_product_id
              ? {
                  ...row,
                  handled: result.handled ? 1 : 0,
                  handled_at: result.handled
                    ? moment().format('YYYY-MM-DD HH:mm:ss')
                    : null
                }
              : row
          )
        }))
      })
      .catch(error => console.error(error))
  }

  const renderTicket = ticket => {
    const handled = isHandledOverpaid(ticket)

    return (
      <tr
        key={ticket.order_product_id}
        className={handled ? 'tools-row--handled' : undefined}
      >
        <td>
          <label>
            <input
              type="checkbox"
              aria-label={`Käsittelytila lipulle ${ticket.original_code}`}
              checked={handled}
              onChange={() => toggleHandled(ticket)}
            />
          </label>
        </td>
        <td>{ticket.original_code}</td>
        <td>
          {ticket.event_name}
          {ticket.event_start_date && (
            <>
              <br />
              <span className="raports-form-hint">
                {formatTimestamp(ticket.event_start_date)}
              </span>
            </>
          )}
        </td>
        <td>
          <a
            href={'https://pipetti.tiketti.fi/tilaus/' + ticket.tiketti_order_id}
            target="_blank"
            rel="noreferrer"
          >
            {ticket.tiketti_order_code}
          </a>
        </td>
        <td> {ticket.price / 100} €</td>
        <td>{ticket.transaction_id}</td>
        <td>{formatTimestamp(ticket.paytrail_payed_at)}</td>
        <td>
          {ticket.first_name} {ticket.last_name}
          <br />
          {ticket.email}
        </td>
        <td>{handled ? formatTimestamp(ticket.handled_at) || 'Käsitelty' : ''}</td>
      </tr>
    )
  }

  return (
    <div>
      <h2>Liput, joissa on liikaa maksuja</h2>
      {data && data.tickets.length === 0 && (
        <div>Ei löytynyt lippuja, joissa olisi liikaa maksuja.</div>
      )}
      <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Käsitelty</th>
            <th>Lippukoodi</th>
            <th>Tapahtuma</th>
            <th>Pipetti Tilaus</th>
            <th>Hinta (€)</th>
            <th>Transaktio ID</th>
            <th>Maksettu</th>
            <th>Asiakas</th>
            <th>Käsitelty</th>
          </tr>
        </thead>
        <tbody>
          {data &&
            data.tickets &&
            data.tickets.map(ticket => renderTicket(ticket))}
        </tbody>
      </table>
      </div>
    </div>
  )
}

export default Tools
